import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, FileText } from "lucide-react";
import { formatDate, formatIDR } from "@/lib/api";
import { toast } from "sonner";

export const soldQuantity = (row) => (row.items || []).reduce((sum, item) => sum + Number(item.qty || 0), 0);

export const RetailExportButtons = ({ rows, from, to, title, storeLabel = "Semua Toko", search = "", testId, disabled = false }) => {
  const [busy, setBusy] = useState(false);
  const invalid = Boolean(from && to && from > to);
  const exportReport = async (format) => {
    if (invalid) return toast.error("Tanggal awal tidak boleh melewati tanggal akhir");
    if (!rows.length) return toast.error("Tidak ada laporan pada filter yang dipilih");
    setBusy(true);
    try {
      const period = `${from ? formatDate(from) : "Awal pencatatan"} s.d. ${to ? formatDate(to) : "Terakhir"}`;
      const filename = `Penjualan_Retail_${from || "awal"}_${to || "terakhir"}`;
      const total = rows.reduce((sum, row) => sum + Number(row.total || 0), 0);
      const qty = rows.reduce((sum, row) => sum + soldQuantity(row), 0);
      const summaries = Object.values(rows.reduce((map, row) => {
        const entry = map[row.store_id] ||= { Toko: row.store_name, Transaksi: 0, "Kuantitas Terjual": 0, "Total Pendapatan": 0 };
        entry.Transaksi++; entry["Kuantitas Terjual"] += soldQuantity(row); entry["Total Pendapatan"] += Number(row.total || 0);
        return map;
      }, {}));
      if (format === "excel") {
        const XLSX = await import("xlsx");
        const book = XLSX.utils.book_new();
        const overview = XLSX.utils.aoa_to_sheet([[title], ["Periode", period], ["Toko", storeLabel], ["Pencarian", search || "Semua"], ["Transaksi", rows.length], ["Kuantitas Terjual", qty], ["Total Diterima", total]]);
        overview["!cols"] = [{ wch: 25 }, { wch: 65 }];
        XLSX.utils.book_append_sheet(book, overview, "Ringkasan");
        const transactions = XLSX.utils.json_to_sheet(rows.map((row, i) => ({
          No: i + 1, Tanggal: row.date?.slice(0, 10), Toko: row.store_name,
          "Kuantitas Terjual": soldQuantity(row), "Metode Bayar": row.payment_method,
          "Total Diterima": Number(row.total || 0), Catatan: row.notes || "",
          Lampiran: row.attachment?.original_filename || "",
        })));
        transactions["!cols"] = [5, 14, 35, 18, 15, 20, 55, 35].map(wch => ({ wch }));
        XLSX.utils.book_append_sheet(book, transactions, "Riwayat Laporan");
        const details = XLSX.utils.json_to_sheet(rows.flatMap((row, i) => (row.items || []).map(item => ({
          "No Laporan": i + 1, Tanggal: row.date?.slice(0, 10), Toko: row.store_name,
          Produk: item.product_name, "Kuantitas Terjual": Number(item.qty), Satuan: item.unit || "pcs",
          "Harga Satuan": Number(item.unit_price), Subtotal: Number(item.qty) * Number(item.unit_price),
        }))));
        details["!cols"] = [12, 14, 35, 40, 18, 10, 20, 20].map(wch => ({ wch }));
        XLSX.utils.book_append_sheet(book, details, "Detail Produk");
        const stores = XLSX.utils.json_to_sheet(summaries);
        stores["!cols"] = [35, 12, 20, 22].map(wch => ({ wch }));
        XLSX.utils.book_append_sheet(book, stores, "Per Toko");
        XLSX.writeFile(book, `${filename}.xlsx`);
      } else {
        const { jsPDF } = await import("jspdf");
        const { autoTable } = await import("jspdf-autotable");
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        doc.setFont("helvetica", "bold"); doc.setFontSize(16);
        doc.text(title, 14, 16);
        doc.setFont("helvetica", "normal"); doc.setFontSize(9);
        const description = doc.splitTextToSize(`Periode: ${period} | Toko: ${storeLabel}${search ? ` | Pencarian: ${search}` : ""}`, 269);
        doc.text(description, 14, 23);
        const y = 23 + description.length * 4.5;
        doc.text(`${rows.length} laporan | Kuantitas terjual: ${qty} | Total diterima: ${formatIDR(total)}`, 14, y);
        autoTable(doc, {
          startY: y + 4, margin: { left: 14, right: 14, bottom: 15 },
          head: [["No", "Tanggal", "Toko", "Produk / Kuantitas", "Qty Terjual", "Metode", "Total Diterima", "Catatan / Lampiran"]],
          body: rows.map((row, i) => [i + 1, formatDate(row.date), row.store_name,
            (row.items || []).map(item => `${item.product_name}: ${item.qty} ${item.unit || "pcs"}`).join("\n"),
            soldQuantity(row), row.payment_method || "cash", formatIDR(row.total),
            [row.notes, row.attachment?.original_filename].filter(Boolean).join("\n") || "-"]),
          foot: [[{ content: "TOTAL", colSpan: 4 }, qty, "", formatIDR(total), ""]],
          showFoot: "lastPage", styles: { fontSize: 8, cellPadding: 2.3, overflow: "linebreak" },
          headStyles: { fillColor: [4, 120, 87] }, footStyles: { fillColor: [235, 245, 240], textColor: [20, 60, 40] },
          columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 24 }, 2: { cellWidth: 40 }, 3: { cellWidth: 58 }, 4: { cellWidth: 20, halign: "right" }, 5: { cellWidth: 20 }, 6: { cellWidth: 34, halign: "right" }, 7: { cellWidth: 63 } },
        });
        for (let page = 1; page <= doc.getNumberOfPages(); page++) {
          doc.setPage(page); doc.setFontSize(8); doc.setTextColor(100);
          doc.text(`Hotto Branch Cianjur | Halaman ${page} / ${doc.getNumberOfPages()}`, 14, 203);
        }
        doc.save(`${filename}.pdf`);
      }
      toast.success(`${format === "excel" ? "Excel" : "PDF"} diunduh (${rows.length} laporan)`);
    } catch (error) { toast.error(`Gagal mengunduh laporan: ${error.message}`); }
    finally { setBusy(false); }
  };
  return <div className="flex flex-wrap gap-2">
    <Button variant="outline" onClick={() => exportReport("excel")} disabled={disabled || busy || invalid || !rows.length} data-testid={`btn-export-excel-${testId}`}><FileSpreadsheet className="w-4 h-4 mr-2"/> Excel</Button>
    <Button variant="outline" onClick={() => exportReport("pdf")} disabled={disabled || busy || invalid || !rows.length} data-testid={`btn-export-pdf-${testId}`}><FileText className="w-4 h-4 mr-2"/> PDF</Button>
  </div>;
};
